import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Popover, Row, Spin, Statistic, Tag } from "antd";
import { useFirestore } from "react-redux-firebase";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { COLORS } from "constants/ChartConstant";
import { FIRESTORE_ANALYSIS_RFM_TABLE } from "../../../../constants/FirestoreConstant";
import RFMChartWidget from "../../../../components/shared-components/RFMChartWidget";
import { useTranslation } from "react-i18next";
import { ArrowLeftOutlined, CalendarOutlined, InfoCircleOutlined } from "@ant-design/icons";

const RFMAnalysisDetail = () => {
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t } = useTranslation();
  const { analysisId } = useParams();
  const navigate = useNavigate();
  const firestore = useFirestore();

  useEffect(() => {
    const fetchAnalysis = async () => {
      try {
        setLoading(true);
        const doc = await firestore.collection(FIRESTORE_ANALYSIS_RFM_TABLE).doc(analysisId).get();

        if (doc.exists) {
          setAnalysisData({ id: doc.id, ...doc.data() });
        } else {
          setError(t("analysis_not_found"));
        }
      } catch (err) {
        console.error("Error fetching analysis:", err);
        setError(t("error_loading_analysis"));
      } finally {
        setLoading(false);
      }
    };

    if (analysisId) {
      fetchAnalysis();
    }
  }, [analysisId, firestore, t]);

  const renderStatisticWithPopover = (title, value, explanationKey, suffix = "") => {
    const popoverContent = <div style={{ maxWidth: 300 }}>{t(explanationKey)}</div>;

    return (
      <Statistic
        title={
          <span>
            {title}
            <Popover content={popoverContent} title={t("explanation")} trigger="hover">
              <InfoCircleOutlined style={{ marginLeft: 8, color: "#1890ff", cursor: "help" }} />
            </Popover>
          </span>
        }
        value={value}
        suffix={suffix}
      />
    );
  };

  const renderDistribution = (data, title) => {
    if (!data) return null;

    const chartData = [
      {
        name: title,
        data: data.map((item) => item.count),
      },
    ];

    const categories = data.map((item) => item.range);

    return (
      <Card title={t(`${title.toLowerCase()}_distribution`)} className="mb-4">
        <RFMChartWidget
          card={false}
          series={chartData}
          xAxis={categories}
          height={300}
          type="bar"
          customOptions={{
            colors: [COLORS[0]],
            xaxis: {
              type: "category",
            },
            yaxis: {
              labels: {
                formatter: (value) => Math.round(value),
              },
            },
            tooltip: {
              y: {
                formatter: (val) => `${val} ${t("customers")}`,
              },
            },
          }}
        />
      </Card>
    );
  };

  const renderPercentiles = (percentiles, title) => {
    if (!percentiles) return null;

    return (
      <Card title={t(`${title.toLowerCase()}_percentiles`)} className="mb-4">
        <Row gutter={16}>
          <Col span={4}>
            <Statistic title={t("min")} value={percentiles.min.toFixed(2)} />
          </Col>
          <Col span={3}>
            <Statistic title="20th" value={percentiles.p20.toFixed(2)} />
          </Col>
          <Col span={3}>
            <Statistic title="40th" value={percentiles.p40.toFixed(2)} />
          </Col>
          <Col span={3}>
            <Statistic title="60th" value={percentiles.p60.toFixed(2)} />
          </Col>
          <Col span={3}>
            <Statistic title="80th" value={percentiles.p80.toFixed(2)} />
          </Col>
          <Col span={4}>
            <Statistic title={t("max")} value={percentiles.max.toFixed(2)} />
          </Col>
        </Row>
      </Card>
    );
  };

  const getPeriodDescription = (period, periodLabel) => {
    if (periodLabel) return periodLabel;

    // Fallback for older analyses without period info
    return t("6_months");
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
        <p style={{ marginTop: "20px" }}>{t("loading_analysis")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "24px" }}>
        <Alert
          message={t("error")}
          description={error}
          type="error"
          showIcon
          action={<Button onClick={() => navigate("/cms/reports/rfm")}>{t("back_to_dashboard")}</Button>}
        />
      </div>
    );
  }

  if (!analysisData) {
    return null;
  }

  return (
    <div style={{ padding: "24px" }}>
      {/* Header */}
      <Row gutter={16} className="mb-4">
        <Col span={20}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/cms/reports/rfm")}
            style={{ marginBottom: "16px" }}
          >
            {t("back_to_dashboard")}
          </Button>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
            <h2 style={{ margin: 0 }}>RFM Analysis - {analysisData.name}</h2>
            <Tag icon={<CalendarOutlined />} color="blue">
              {getPeriodDescription(analysisData.period, analysisData.periodLabel)}
            </Tag>
          </div>
          <p>
            {t("analysis_date")}: {dayjs.unix(analysisData.timestamp).format("MMMM D, YYYY HH:mm")}
          </p>
        </Col>
      </Row>

      {/* Summary Statistics */}
      <Row gutter={16} className="mb-4">
        <Col span={6}>
          <Card>
            {renderStatisticWithPopover(
              t("active_customers"),
              analysisData.totalCustomers,
              "explanations.explanation_active_customers",
            )}
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            {renderStatisticWithPopover(
              t("total_users_analyzed"),
              analysisData.totalActiveCustomers,
              "explanations.explanation_total_users_analyzed",
            )}
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            {renderStatisticWithPopover(
              t("orders_analyzed"),
              analysisData.totalOrdersAnalyzed,
              "explanations.explanation_orders_analyzed",
            )}
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            {renderStatisticWithPopover(
              t("activation_rate"),
              analysisData.activationRate,
              "explanations.explanation_activation_rate",
              "%",
            )}
          </Card>
        </Col>
      </Row>

      {/* Guest vs Customer Orders Stats */}
      {analysisData.orderStats && (
        <Row gutter={16} className="mb-4">
          <Col span={6}>
            <Card>
              {renderStatisticWithPopover(
                t("customer_orders"),
                `${analysisData.orderStats.customerOrders} (${analysisData.orderStats.customerPercentage}%)`,
                "explanations.explanation_customer_orders",
              )}
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              {renderStatisticWithPopover(
                t("guest_orders"),
                `${analysisData.orderStats.guestOrders} (${analysisData.orderStats.guestPercentage}%)`,
                "explanations.explanation_guest_orders",
              )}
            </Card>
          </Col>
        </Row>
      )}

      {/* Distribution Charts */}
      {analysisData.distributions && (
        <Row gutter={16}>
          <Col span={24}>
            {renderDistribution(analysisData.distributions.recency, "Recency")}
            {renderPercentiles(analysisData.percentiles.recency, "Recency")}
          </Col>
          <Col span={24}>
            {renderDistribution(analysisData.distributions.frequency, "Frequency")}
            {renderPercentiles(analysisData.percentiles.frequency, "Frequency")}
          </Col>
          <Col span={24}>
            {renderDistribution(analysisData.distributions.monetary, "Monetary")}
            {renderPercentiles(analysisData.percentiles.monetary, "Monetary")}
          </Col>
        </Row>
      )}

      {/* Analysis Info */}
      <Card title={t("analysis_information")} className="mt-4">
        <Row gutter={16}>
          <Col span={12}>
            <p>
              <strong>{t("analysis_id")}:</strong> {analysisData.id}
            </p>
            <p>
              <strong>{t("analysis_date")}:</strong> {dayjs.unix(analysisData.timestamp).format("MMMM D, YYYY HH:mm")}
            </p>
            <p>
              <strong>{t("data_period")}:</strong> {getPeriodDescription(analysisData.period, analysisData.periodLabel)}
            </p>
          </Col>
          <Col span={12}>
            <p>
              <strong>{t("customers_with_orders")}:</strong> {analysisData.totalCustomers}
            </p>
            <p>
              <strong>{t("total_registered_users")}:</strong> {analysisData.totalActiveCustomers}
            </p>
            <p>
              <strong>{t("orders_in_period")}:</strong> {analysisData.totalOrdersAnalyzed}
            </p>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default RFMAnalysisDetail;
